import Phaser from 'phaser';
import { SCENE, STREET_SPAWNS } from '../config.js';
import { gameState } from '../state/gameState.js';
import { MoneyBar } from '../ui/MoneyBar.js';
import { showToast } from '../ui/Toast.js';
import { messageFor } from '../api/errors.js';
import { attachCoordsDebug } from '../ui/CoordsDebug.js';
import { reportLocation } from '../ui/locationTracker.js';
import { queueMissing, loadCtx, prioritiseFor } from '../ui/sceneLoading.js';
import { needsFor } from '../assetManifest.mjs';
import { iconText } from '../ui/iconText.js';
import { gameButton, gameCard } from '../ui/gameUi.js';
import { FONT } from '../ui/gameUiStyle.mjs';
import { shopRoom } from '../ui/shopInterior.js';
import { SMALL_PANEL } from '../ui/shopGrid.mjs';
import { sellAllPlan } from '../shop/sellAll.mjs';

// Warzywniak sells what the animals make. Crops are sold on the farm the moment they are
// harvested and seeds are bought there (spec 2026-09-22 §3), so the Nasionka half is gone.
const PRODUCT_IDS = ['jajko', 'mleko'];
// Handoff 1c: the smaller panel and two product cards, the buttons are the tap targets.
const CARD_W = 400, CARD_H = 226, GAP = 24, CARD_TOP = SMALL_PANEL.y - 148;

export class ShopScene extends Phaser.Scene {
  constructor() { super(SCENE.SHOP); }

  preload() {
    queueMissing(this, needsFor(this.scene.key, loadCtx()));
    prioritiseFor(this.scene.key);
  }

  create() {
    const { layer } = shopRoom(this, {
      bgKey: 'bg_shop', title: '{egg} Sprzedaż', panel: SMALL_PANEL,
      onExit: () => this.scene.start(SCENE.OUTSIDE, { spawnX: STREET_SPAWNS.SHOP }),
    });
    const left0 = SMALL_PANEL.x - (PRODUCT_IDS.length * CARD_W + (PRODUCT_IDS.length - 1) * GAP) / 2;
    this.sellCards = PRODUCT_IDS.map((p, i) => this._makeSellCard(layer, p, left0 + i * (CARD_W + GAP), CARD_TOP));

    new MoneyBar(this);
    this._refresh();
    this._unsub = gameState.on(() => this._refresh());
    this.events.on('shutdown', () => this._unsub?.());
    reportLocation(this, null, null, { immediate: true });
    attachCoordsDebug(this);
  }

  _makeSellCard(layer, item, left, top) {
    const card = gameCard(this, left + CARD_W / 2, top + CARD_H / 2, CARD_W, CARD_H);
    const tile = this.add.graphics();
    tile.fillStyle(0xfdf0ec).fillRoundedRect(left + 16, top + 18, 104, 104, 14);
    const tex = `product_${item}`;
    const icon = this.add.image(left + 68, top + 70, this.textures.exists(tex) ? tex : 'plot_empty');
    icon.setScale(Math.min(84 / icon.width, 84 / icon.height));
    const textX = left + 136;
    const name = iconText(this, textX, top + 46, '', { fontFamily: FONT, fontSize: '24px', color: '#000', fontStyle: 'bold' }).setOrigin(0, 0.5);
    const price = iconText(this, textX, top + 74, '', { fontFamily: FONT, fontSize: '17px', color: '#3f9a2a', fontStyle: 'bold' }).setOrigin(0, 0.5);
    const per = iconText(this, 0, top + 75, 'za sztukę', { fontFamily: FONT, fontSize: '15px', color: '#6b5a44' }).setOrigin(0, 0.5);
    const haveLabel = iconText(this, textX, top + 98, 'masz:', { fontFamily: FONT, fontSize: '15px', color: '#555' }).setOrigin(0, 0.5);
    const have = iconText(this, haveLabel.x + haveLabel.width + 5, top + 98, '', { fontFamily: FONT, fontSize: '15px', color: '#000', fontStyle: 'bold' }).setOrigin(0, 0.5);
    // "Sprzedaj" sells one; beside it "Wszystko" sells this product's whole stock and shows what that
    // earns (owner 2026-10-05: one per card, not one big button for both)
    const btnY = top + 161;
    const btn = gameButton(this, left + 95, btnY, 150, 48, 'Sprzedaj', async () => {
      try { await gameState.sellProduct(item, 1); showToast(this, 'Sprzedano {check}'); }
      catch (e) { showToast(this, messageFor(e)); }
    }, { variant: 'primary', fontSize: 17 });
    const allBtn = gameButton(this, left + 281, btnY, 198, 48, '', async () => {
      if (this._selling) return;
      const plan = sellAllPlan(gameState.raw?.inventory?.products, gameState.raw?.catalog?.products, [item]);
      if (!plan.requests.length) return;
      this._selling = true;
      try {
        for (const r of plan.requests) await gameState.sellProduct(r.item, r.qty);
        showToast(this, `Sprzedano wszystko: +${plan.total} {coin}`);
      } catch (e) { showToast(this, messageFor(e)); }
      finally { this._selling = false; this._refresh(); }
    }, { variant: 'secondary', fontSize: 16 });
    layer.add([card, tile, icon, name, price, per, haveLabel, have, btn, allBtn]);
    return { item, name, price, per, have, btn, allBtn };
  }

  _refresh() {
    const productsCat = gameState.raw?.catalog?.products ?? {};
    const inv = gameState.raw?.inventory ?? {};
    for (const c of this.sellCards) {
      const spec = productsCat[c.item];
      const owned = inv.products?.[c.item] ?? 0;
      c.name.setText(spec?.name_pl ?? c.item);
      c.price.setText(`+${spec?.price ?? 0} {coin}`);
      c.per.setX(c.price.x + c.price.width + 6);
      c.have.setText(String(owned));
      c.btn.setEnabled(owned > 0);
      const { total } = sellAllPlan(inv.products, productsCat, [c.item]);
      c.allBtn.setLabel(`Wszystko +${total} {coin}`).setEnabled(total > 0);
    }
  }
}
